import React, { useState, useRef, useEffect } from "react";
import { IOption } from "../../../models/IOption";

interface OwMultiSelectOptionProps {
    options: IOption[];
    selectedOptions?: string[];
    onChange?: (selected: string[]) => void;
    placeholder?: string;
    maxDisplay?: number;
    className?: string;
    style?: React.CSSProperties;
    dropdownWidth?: string | number;
    inputClassName?: string;
    labelClassName?: string;
}

export const OwMultiSelectOption: React.FC<OwMultiSelectOptionProps> = ({
    options,
    selectedOptions = [],
    onChange,
    placeholder = "Select options",
    maxDisplay = 2,
    className = "",
    style = {},
    dropdownWidth = "100%",
    inputClassName = "",
    labelClassName = "",
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<string[]>(selectedOptions);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const arraysEqualIgnoreOrder = (arr1: string[], arr2: string[]): boolean => {
        if (arr1.length !== arr2.length) return false;

        const sortedArr1 = [...arr1].sort();
        const sortedArr2 = [...arr2].sort();

        return sortedArr1.every((val, index) => val === sortedArr2[index]);
    };

    // Handle outside click to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!arraysEqualIgnoreOrder(selected, selectedOptions)) {
            setSelected([...selectedOptions]);
            setIsDirty(true);
        }
    }, [selectedOptions]);

    // Update parent component when selection changes
    useEffect(() => {
        if (onChange && isDirty) {
            onChange(selected);
            setIsDirty(false);
        }
    }, [selected]);

    const toggleDropdown = (): void => {
        setIsOpen(!isOpen);
    };

    const toggleOption = (option: IOption): void => {
        setSelected((prev) => {
            if (prev.includes(option.value)) {
                return prev.filter((value) => value !== option.value);
            } else {
                return [...prev, option.value];
            }
        });

        setIsDirty(true);
    };

    // Visual representation of selected options
    const renderSelectionPills = (): React.ReactNode => {
        if (selected.length === 0) {
            return <span className="text-muted">{placeholder}</span>;
        }

        const displayCount = Math.min(selected.length, maxDisplay);
        const hasMore = selected.length > maxDisplay;

        return (
            <>
                {selected.slice(0, displayCount).map((value) => {
                    // Find the matching option to get the label
                    const option = options.find((opt) => opt.value === value);
                    return (
                        <span key={value} className="badge bg-primary text-light me-2 py-1 px-3 rounded-pill">
                            {option ? option.label : value}
                        </span>
                    );
                })}
                {hasMore && <span className="badge bg-primary text-light py-1 px-3 rounded-pill">+{selected.length - maxDisplay}</span>}
            </>
        );
    };

    return (
        <div
            className={`ow-multi-select-container position-relative ${className}`}
            ref={dropdownRef}
            style={{ ...style, width: dropdownWidth }}>
            {/* Selected items display */}
            <div
                className="form-control form-control-sm d-flex align-items-center flex-wrap gap-1 "
                onClick={toggleDropdown}
                style={{ cursor: "pointer", overflow: "hidden" }}>
                {renderSelectionPills()}
                <span className="ms-auto">
                    <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`}></i>
                </span>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div
                    className="position-absolute start-0 end-0 mt-1 border rounded bg-white shadow-sm z-3 p-2"
                    style={{ maxWidth: "100%", minWidth: "100%", overflowX: "hidden" }}>
                    <div>
                        <div className="form-control-plaintext">
                            {options.map((option) => (
                                <div key={option.value} className="form-check">
                                    <input
                                        className={`form-check-input pointer ${inputClassName}`}
                                        type="checkbox"
                                        id={`option-${option.value}`}
                                        checked={selected.includes(option.value)}
                                        onChange={() => toggleOption(option)}
                                    />
                                    <label
                                        className={`form-check-label ms-2 text-truncate pointer ${labelClassName}`}
                                        htmlFor={`option-${option.value}`}
                                        title={option.label}
                                        style={{ maxWidth: "calc(100% - 25px)" }}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
